// The interviewer sees ONE experience where the records are two (V1-INTERACTIONS §3.2 identity is unchanged; the
// presentation layer is documented in §4.11). EM-14 / AF-15: packet and scorecard collapse into the three states
// Not Started · Open · Submitted, derived from the interview's scorecard state and whether the packet was opened.

import { RECOMMENDATION_LABELS, recommendationWord } from "@/mc/labels";

/** One receipt wording for the packet's question channel, whichever screen it is sent from (E-39, EM-10). */
export const ASK_RECEIPT: Record<"hiring_manager" | "recruiter", string> = {
  hiring_manager: "Sent — the Hiring Manager has it on Today; unanswered after a business day it is re-raised.",
  recruiter: "Sent — the Recruiter has it on Today; unanswered after a business day it is re-raised.",
};

export type InterviewState = "not_started" | "open" | "submitted";

export const INTERVIEW_STATE: readonly InterviewState[] = ["not_started", "open", "submitted"] as const;

export const INTERVIEW_STATE_LABEL: Record<InterviewState, string> = {
  not_started: "Not Started",
  open: "Open",
  submitted: "Submitted",
};

/** Eduardo's vocabulary for the retained record beneath the state — never the raw database enum (R-INT-52). */
export const SCORECARD_STATE_LABEL: Record<string, string> = {
  draft: "Draft",
  submitted: "Submitted",
  frozen: "Frozen",
  frozen_with_addenda: "Frozen with addendum",
};

const SUBMITTED_STATES = new Set(["submitted", "frozen", "frozen_with_addenda"]);

export function scorecardStateLabel(state: string | null | undefined): string | null {
  if (!state) return null;
  return SCORECARD_STATE_LABEL[state] ?? state.replace(/_/g, " ");
}

/**
 * Not Started — no draft and the packet was never opened · Open — a draft exists or the packet has been opened ·
 * Submitted — the author's assessment is in (including the debrief's freeze, which is still "submitted" to them).
 */
export function interviewState(scorecardState: string | null | undefined, firstOpenedAt: string | null | undefined): InterviewState {
  if (scorecardState && SUBMITTED_STATES.has(scorecardState)) return "submitted";
  if (scorecardState === "draft" || firstOpenedAt) return "open";
  return "not_started";
}

/** The own-result line's recommendation word (B14, C5): the one mapping's label, and the earlier-scale marker beside a
 *  card recorded on the earlier scale; an unknown value is drawn as nothing rather than guessed. */
export function ownRecommendationLabel(value: unknown): string | null {
  const word = typeof value === "string" ? recommendationWord(value) : null;
  if (!word) return null;
  return word.earlierScale ? `${word.label} · ${RECOMMENDATION_LABELS.earlierScale}` : word.label;
}

/** "4.2 / 5 | Strong Hire" — the interviewer's OWN result, only after their own submission (AF-14). */
export function ownResult(average: string | null | undefined, recommendationLabel: string | null | undefined): string | null {
  if (!average && !recommendationLabel) return null;
  if (!average) return recommendationLabel ?? null;
  return recommendationLabel ? `${average} / 5 | ${recommendationLabel}` : `${average} / 5`;
}
